<template>
  <div class="cosmic-background" ref="containerRef">
    <!-- Canvas for particles -->
    <canvas ref="canvasRef" class="cosmic-canvas"></canvas>

    <!-- Gradient overlay -->
    <div class="cosmic-gradient"></div>

    <!-- Floating orbs -->
    <div class="cosmic-orbs">
      <div class="orb orb-1"></div>
      <div class="orb orb-2"></div>
      <div class="orb orb-3"></div>
    </div>

    <!-- Grid pattern -->
    <div class="cosmic-grid"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const canvasRef = ref(null)
const containerRef = ref(null)
let animationFrame = null
let particles = []
const darkPalette = [
  { r: 167, g: 139, b: 250 },
  { r: 56, g: 189, b: 248 },
  { r: 129, g: 140, b: 248 }
]
const lightPalette = [
  { r: 139, g: 92, b: 246 },
  { r: 99, g: 102, b: 241 },
  { r: 59, g: 130, b: 246 }
]

onMounted(() => {
  initCanvas()
  initParticles()
  animate()

  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  if (animationFrame) {
    cancelAnimationFrame(animationFrame)
  }
  window.removeEventListener('resize', handleResize)
})

const initCanvas = () => {
  const canvas = canvasRef.value
  const container = containerRef.value

  if (canvas && container) {
    canvas.width = container.offsetWidth
    canvas.height = container.offsetHeight
  }
}

const initParticles = () => {
  const canvas = canvasRef.value
  if (!canvas) return

  particles = []
  const particleCount = 120

  for (let i = 0; i < particleCount; i++) {
    const palette = isDarkMode() ? darkPalette : lightPalette
    particles.push({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: Math.random() * 2 + 0.5,
      speedX: (Math.random() - 0.5) * 0.5,
      speedY: (Math.random() - 0.5) * 0.5,
      opacity: Math.random() * 0.5 + 0.2,
      twinkleOffset: Math.random() * Math.PI * 2,
      color: palette[Math.floor(Math.random() * palette.length)]
    })
  }
}

const isDarkMode = () => {
  if (typeof document === 'undefined') return true
  return document.documentElement.classList.contains('dark') || document.body.classList.contains('dark')
}

const animate = () => {
  const canvas = canvasRef.value
  const ctx = canvas?.getContext('2d')

  if (!ctx || !canvas) return

  const darkMode = isDarkMode()
  const time = performance.now()
  const connectionDistance = darkMode ? 130 : 118

  ctx.clearRect(0, 0, canvas.width, canvas.height)

  // Draw particles
  particles.forEach(particle => {
    // Update position
    particle.x += particle.speedX
    particle.y += particle.speedY

    // Wrap around edges
    if (particle.x < 0) particle.x = canvas.width
    if (particle.x > canvas.width) particle.x = 0
    if (particle.y < 0) particle.y = canvas.height
    if (particle.y > canvas.height) particle.y = 0

    // Draw particle
    ctx.beginPath()
    ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2)
    const pulse = 0.78 + Math.sin(time * 0.002 + particle.twinkleOffset) * 0.22
    const alpha = Math.min(1, particle.opacity * pulse * (darkMode ? 1.22 : 1))
    const { r, g, b } = particle.color
    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`
    ctx.fill()
  })

  // Draw connections
  particles.forEach((p1, i) => {
    particles.slice(i + 1).forEach(p2 => {
      const dx = p1.x - p2.x
      const dy = p1.y - p2.y
      const distance = Math.sqrt(dx * dx + dy * dy)

      if (distance < connectionDistance) {
        ctx.beginPath()
        ctx.moveTo(p1.x, p1.y)
        ctx.lineTo(p2.x, p2.y)
        const baseOpacity = darkMode ? 0.16 : 0.125
        const { r, g, b } = p1.color
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${baseOpacity * (1 - distance / connectionDistance)})`
        ctx.lineWidth = darkMode ? 0.9 : 0.6
        ctx.stroke()
      }
    })
  })

  animationFrame = requestAnimationFrame(animate)
}

const handleResize = () => {
  initCanvas()
  initParticles()
}
</script>

<style scoped>
.cosmic-background {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.cosmic-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.cosmic-gradient {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 80% 50% at 50% -20%, rgba(139, 92, 246, 0.3), transparent),
    radial-gradient(ellipse 60% 40% at 80% 50%, rgba(37, 99, 235, 0.2), transparent),
    radial-gradient(ellipse 60% 40% at 20% 80%, rgba(245, 158, 11, 0.1), transparent);
  animation: gradient-shift 15s ease infinite;
  background-size: 200% 200%;
}

.dark .cosmic-gradient {
  background:
    radial-gradient(ellipse 90% 55% at 50% -18%, rgba(56, 189, 248, 0.26), transparent 58%),
    radial-gradient(ellipse 72% 48% at 84% 22%, rgba(129, 140, 248, 0.28), transparent 62%),
    radial-gradient(ellipse 58% 42% at 16% 78%, rgba(167, 139, 250, 0.22), transparent 66%);
  opacity: 0.88;
}

@keyframes gradient-shift {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.cosmic-orbs {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  animation: float-orb 20s ease-in-out infinite;
}

.dark .orb {
  filter: blur(66px);
}

.orb-1 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(139, 92, 246, 0.4) 0%, transparent 70%);
  top: -100px;
  right: -100px;
  animation-delay: 0s;
}

.orb-2 {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(37, 99, 235, 0.3) 0%, transparent 70%);
  bottom: -50px;
  left: -50px;
  animation-delay: -7s;
}

.orb-3 {
  width: 350px;
  height: 350px;
  background: radial-gradient(circle, rgba(245, 158, 11, 0.2) 0%, transparent 70%);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation-delay: -14s;
}

.dark .orb-1 {
  background: radial-gradient(circle, rgba(129, 140, 248, 0.42) 0%, transparent 72%);
}

.dark .orb-2 {
  background: radial-gradient(circle, rgba(56, 189, 248, 0.34) 0%, transparent 72%);
}

.dark .orb-3 {
  background: radial-gradient(circle, rgba(167, 139, 250, 0.3) 0%, transparent 72%);
}

@keyframes float-orb {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  25% {
    transform: translate(30px, -30px) scale(1.05);
  }
  50% {
    transform: translate(-20px, 20px) scale(0.95);
  }
  75% {
    transform: translate(-30px, -20px) scale(1.02);
  }
}

.cosmic-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(139, 92, 246, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(139, 92, 246, 0.03) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: grid-move 20s linear infinite;
}

.dark .cosmic-grid {
  background-image:
    linear-gradient(rgba(129, 140, 248, 0.085) 1px, transparent 1px),
    linear-gradient(90deg, rgba(56, 189, 248, 0.08) 1px, transparent 1px);
  opacity: 0.46;
}

@keyframes grid-move {
  0% {
    transform: perspective(500px) rotateX(60deg) translateY(0);
  }
  100% {
    transform: perspective(500px) rotateX(60deg) translateY(50px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .cosmic-gradient,
  .orb,
  .cosmic-grid {
    animation: none;
  }
}
</style>
